/**
 * Login Action Creators
 *
 * @module actions/Login
 */
import { createAction } from 'typesafe-actions';

import { LoginActions } from '../constants/Login';

type LoginFlowCompleteParams = boolean;
interface LoginFlowCompletePayload {
  shouldPreserveLoginRedirect: LoginFlowCompleteParams;
}

/**
 * Login flow was completed
 *
 * @param {boolean} shouldPreserveLoginRedirect
 */
export const loginFlowComplete = createAction(
  LoginActions.LOGIN_FLOW_COMPLETE,
  (shouldPreserveLoginRedirect: LoginFlowCompleteParams) => ({
    shouldPreserveLoginRedirect,
  }),
)<LoginFlowCompletePayload>();

export type LoginFlowCompleteAction = ReturnType<typeof loginFlowComplete>;

type RememberLoginParams = boolean;
interface RememberLoginPayload {
  remember: RememberLoginParams;
}

/**
 * rememberLogin
 *
 * @param {boolean} [remember] - whether to remember login
 */
export const rememberLogin = createAction(
  LoginActions.LOGIN_REMEMBER,
  (remember: RememberLoginParams) => ({
    remember,
  }),
)<RememberLoginPayload>();

export type RememberLoginAction = ReturnType<typeof rememberLogin>;

/**
 * Called to reset login state meaningful during login operation
 */
export const loginReset = createAction(LoginActions.LOGIN_RESET)();
export type LoginResetAction = ReturnType<typeof loginReset>;

type LoginRedirectOnSuccessParams = string | null;
interface LoginRedirectOnSuccessPayload {
  afterLoginRedirect: LoginRedirectOnSuccessParams;
}

/**
 * Sets the location app should redirect after login
 *
 * @param {string | null} afterLoginRedirect - path to redirect to
 */
export const setAfterLoginRedirect = createAction(
  LoginActions.LOGIN_REDIRECT_ON_SUCCESS,
  (afterLoginRedirect: LoginRedirectOnSuccessParams) => ({
    afterLoginRedirect,
  }),
)<LoginRedirectOnSuccessPayload>();
export type LoginRedirectOnSuccessAction = ReturnType<typeof setAfterLoginRedirect>;

type SetResetUserNameParams = string;
interface SetResetUserNamePayload {
  username: SetResetUserNameParams;
}

/**
 * During the forgot password flow, set the username that the forgot password
 * email was sent to
 *
 * @param {string} username - the username to set
 */
export const setResetUsername = createAction(
  LoginActions.SET_RESET_USERNAME,
  (username: SetResetUserNameParams) => ({
    username,
  }),
)<SetResetUserNamePayload>();

export type SetResetUserNameAction = ReturnType<typeof setResetUsername>;

/**
 * During the forgot password flow, reset the success state
 */
export const resetForgotPasswordSuccessState = createAction(
  LoginActions.RESET_EMAIL_PASSWORD_SUCCESS,
)();
export type ResetEmailPasswordSuccessAction = ReturnType<typeof resetForgotPasswordSuccessState>;

export const setAuthTokenNull = createAction(LoginActions.SET_AUTH_TOKEN_NULL)();
export type SetAuthTokenNullAction = ReturnType<typeof setAuthTokenNull>;

interface ResetPasswordParams {
  newPassword: string;
  resetToken: string;
}
type ResetPasswordPayload = ResetPasswordParams;

/**
 * Sends the api request for resetting a user's password, then redirects on success
 *
 * @param {string} newPassword - the password to set
 * @param {string} resetToken - the token to use to reset password
 */
export const resetUserPassword = createAction(
  LoginActions.RESET_PASSWORD,
  (params: ResetPasswordParams) => params,
)<ResetPasswordPayload>();
export type ResetPasswordAction = ReturnType<typeof resetUserPassword>;

interface AuthenticateListenerParams {
  accessToken: string | null;
  authToken: string | null;
  deviceUuid: string;
  isAnon: boolean;
  isCustomerServiceLogin: boolean;
  isLoginAttempt: boolean;
  isReauth: boolean;
  isUserInitiated: boolean;
  password: string;
  rememberMe: boolean;
  router: string | null;
  username: string;
}
type AuthenticateListenerPayload = AuthenticateListenerParams;

/**
 * Authenticate Listener
 *
 * @param {string} [accessToken] - the access token of the listener to log in
 * @param {string} [authToken] - authToken, used in the case of anon login
 * @param {string} [deviceUuid] - the device uuid used to pass into the deviceLogin API
 * @param {boolean} [isAnon] - is user anon
 * @param {boolean} [isCustomerServiceLogin] [description]
 * @param {boolean} [isLoginAttempt] - true when a successful login attempt should consider the user as authenticated. false when a successful login attempt should not be considered authenticated
 * @param {boolean} [isReauth] - whether or not this request is to be considered a reauth
 * @param {boolean} [isUserInitiated] - is this attempt initiated by a user, or an automatic auth attempt
 * @param {string} password - password
 * @param {boolean} [rememberMe] - whether to remember login
 * @param {object} [router] - routing to use for redirect
 * @param {string} username - username
 */
export const authenticateListener = createAction(
  LoginActions.AUTHENTICATE_LISTENER,
  ({
    accessToken = null,
    authToken = null,
    deviceUuid = '',
    isAnon = false,
    isCustomerServiceLogin = false,
    isLoginAttempt = true,
    isReauth = false,
    isUserInitiated = false,
    password = '',
    rememberMe = false,
    router = null,
    username = '',
  }) => ({
    accessToken,
    authToken,
    deviceUuid,
    isAnon,
    isCustomerServiceLogin,
    isLoginAttempt,
    isReauth,
    isUserInitiated,
    password,
    rememberMe,
    router,
    username,
  }),
)<AuthenticateListenerPayload>();
export type AuthenticateListenerAction = ReturnType<typeof authenticateListener>;

type SetGenderParams = $TSFixMe;
interface SetGenderPayload {
  gender: SetGenderParams;
}

export const setGender = createAction(LoginActions.SET_GENDER, (gender: SetGenderParams) => ({
  gender,
}))<SetGenderPayload>();
export type SetGenderAction = ReturnType<typeof setGender>;

type SetStashedExistingAuthTokenParams = string | null;

export const setStashedExistingAuthToken = createAction(
  LoginActions.SET_STASHED_EXISTING_AUTH_TOKEN,
  (payload: SetStashedExistingAuthTokenParams) => payload,
)<SetStashedExistingAuthTokenParams>();
export type SetStashedExistingAuthTokenAction = ReturnType<typeof setStashedExistingAuthToken>;
